import { EditableText } from "@/components/EditableText";
import { ExternalFileBrowserDialog } from "@/components/ExternalConnections/ExternalFileBrowserDialog";
import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { AppTopRightMenu } from "@/components/app-layout/AppTopRightMenu";
import { BaseDialog } from "@/components/dialogs/BaseDialog";
import { DeleteDialog } from "@/components/dialogs/DeleteDialog";
import { ArtifactMetadata } from "@/components/display/attributes/ArtifactMetadata";
import { ExpertIcon } from "@/components/display/display-icon/artifacts";
import { FileCountBadge } from "@/components/folder/FileCountBadge";
import {
  FileUploadDialog,
  FileUploadDialogRef,
} from "@/components/folder/FileUploadDialog";
import { FolderFiles } from "@/components/folder/FolderFiles";
import { SubFolders } from "@/components/folder/SubFolders";
import { RoleGate } from "@/components/gate/RoleGate";
import { GrantDialog } from "@/components/grant/GrantDialog";
import { AddFromLibraryDialog } from "@/components/library/AddFromLibraryDialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useExpertsData } from "@/data/use-experts-data";
import { useKitFoldersData } from "@/data/use-kit-folders-data";
import { useKitData } from "@/data/use-kits-data";
import { useGroupSlug } from "@/hooks/use-group-slug";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useKitUuid } from "@/hooks/use-kit-uuid";
import {
  groupKitFolderExternalFileControllerLinkExternalFile,
  groupKitFolderExternalFileControllerLinkExternalFilesBatch,
} from "@/lib/gen-api";

import { CollapsibleText } from "@/components/CollapsibleText";
import { Markdown } from "@/components/markdown/Markdown";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useFolderData } from "@/data/use-folder-data";
import { useFoldersData } from "@/data/use-folders-data";
import { useGrantsData } from "@/data/use-grants-data";
import { useKitFolderData } from "@/data/use-kit-folder-data";
import { smartDate } from "@/lib/date";
import { canWriteArtifact, isOwnedArtifact } from "@/lib/grant";
import { EXTERNAL_FOLDER_SYNC_PROVIDERS } from "@/lib/nango-integrations.constants";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";
import { Folder } from "@/types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGoogleDrive,
  faMicrosoft,
} from "@nnr/fontawesome-classic-brands/icons/classic/brands";
import {
  faBooks,
  faChevronRight,
  faCloud,
  faEllipsisV,
  faFile,
  faFolders,
  faGear,
  faLock,
  faPlus,
  faRefresh,
  faSync,
  faTrash,
  faUnlink,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { toast } from "sonner";

export const FolderPage = () => {
  const groupSlug = useGroupSlug();
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const params = useParams({ strict: false }) as Record<
    string,
    string | undefined
  >;
  const folderUuid = params.folderUuid;
  const splatPath = params["_splat"];
  const subFolderPath = splatPath ? splatPath.split("/").filter(Boolean) : [];
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Data
  const { folder: kitFolder } = useKitFolderData({
    kitUuid,
    folderUuid: folderUuid ?? null,
  });
  const loadFiles = kitFolder?.readonly === false && kitFolder.access !== null;
  const {
    folders: kitFolders,
    deleteFolder,
    updateFolder,
  } = useKitFoldersData(groupUuid, kitUuid);
  const {
    folder: rootFolder,
    files,
    folders,
    foldersMap,
    getSubFolder,
    getSubfolders,
    parseRemainingFiles,
    syncFolder,
    isSyncingFolder,
    isAnyFilePending,
  } = useFolderData(groupUuid, kitUuid, folderUuid ?? null, {
    enabled: loadFiles,
  });

  const folder =
    subFolderPath.length > 0 && rootFolder
      ? (getSubFolder(subFolderPath[subFolderPath.length - 1]) ?? rootFolder)
      : rootFolder;

  const subFolders = getSubfolders(folder?.uuid ?? null);

  const { kit, unlinkFolder } = useKitData(groupUuid, kitUuid);
  const { experts: creatorExperts } = useExpertsData(
    groupUuid,
    kit && kit.type === "expert" ? kit.uuid : null,
  );

  const { grants } = useGrantsData({
    folderUuid: kitFolder?.uuid ?? null,
  });
  const sharedGroups = grants.filter((grant) => grant.access !== "owner");

  // Dialogs
  const fileUploadDialogRef = useRef<FileUploadDialogRef>(null);
  const [dialogOpen, setDialogOpen] = useState<
    "external-file-dialog" | "delete" | "unlink" | null
  >(null);
  const [addFromLibraryOpen, setAddFromLibraryOpen] = useState(false);

  const creatorExpert =
    creatorExperts.length > 0 ? creatorExperts[0] : undefined;
  const {
    folders: expertFolders,
    associateFolder,
    dissociateFolder,
  } = useFoldersData({
    expertUuid:
      kit?.type === "expert"
        ? creatorExpert !== undefined
          ? creatorExpert.uuid
          : null
        : null,
  });
  const isExpertFolder = expertFolders.some(
    (expertFolder) => expertFolder.uuid === (folder?.uuid ?? kitFolder?.uuid),
  );
  const childFolders = Object.values(folders).filter(
    (f) => f.parentFolderUuid === folder?.uuid,
  );

  if (!kitFolder || !groupUuid || !groupSlug || !kitUuid) {
    return null;
  }

  const handleUpdateTitle = async (newTitle: string) => {
    await updateFolder({
      folderUuid: kitFolder.uuid,
      name: newTitle,
    });
  };

  const handleUpdateDescription = async (newDescription: string) => {
    await updateFolder({
      folderUuid: kitFolder.uuid,
      description: newDescription,
    });
  };

  const handleUploadClick = () => {
    fileUploadDialogRef.current?.openFileDialog();
  };

  const handleExternalFileSelected = async (
    files: {
      connectionUuid: string;
      externalId: string;
      name: string;
      driveId?: string;
    }[],
  ) => {
    if (!groupUuid || !folder || files.length === 0) return;

    try {
      if (files.length === 1) {
        const file = files[0]!;
        const response =
          await groupKitFolderExternalFileControllerLinkExternalFile({
            path: {
              groupUuid,
              kitUuid,
              folderUuid: folder.uuid,
            },
            body: {
              connectionUuid: file.connectionUuid,
              externalId: file.externalId,
              driveId: file.driveId,
            },
          });
        if (response.error) {
          throw new Error(String(response.error));
        }
        toast.success(`File "${file.name}" linked successfully!`);
      } else {
        const response =
          await groupKitFolderExternalFileControllerLinkExternalFilesBatch({
            path: {
              groupUuid,
              kitUuid,
              folderUuid: folder.uuid,
            },
            body: {
              connectionUuid: files[0]!.connectionUuid,
              files: files.map((f) => ({
                externalId: f.externalId,
                driveId: f.driveId,
              })),
            },
          });
        if (response.error) {
          throw new Error(String(response.error));
        }
        const linked = response.data?.linked.length ?? 0;
        const failed = response.data?.failedCount ?? 0;
        if (failed > 0) {
          toast.warning(
            `Linked ${linked} file(s); ${failed} failed.${response.data?.firstError ? ` ${response.data.firstError}` : ""}`,
          );
        } else {
          toast.success(`Linked ${linked} files successfully!`);
        }
      }
      setDialogOpen(null);
      if (kitUuid) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.kitFolders(groupUuid, kitUuid),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.kitFolder(groupUuid, kitUuid, folder.uuid),
        });
      }
    } catch (error) {
      const detail = error instanceof Error ? error.message : "Unknown error";
      toast.error(`Failed to link external file(s): ${detail}`);
      console.error("Failed to link external file:", error);
    }
  };

  const navigateToFirstFolderOrKitPage = () => {
    const remainingFolders = Object.values(kitFolders).filter(
      (f) => f.uuid !== kitFolder.uuid,
    );
    if (remainingFolders.length > 0) {
      navigate({
        to: "/w/$groupSlug/k/$kitUuid/knowledge/$folderUuid/$",
        params: {
          groupSlug,
          kitUuid,
          folderUuid: remainingFolders[0].uuid,
          _splat: "",
        },
      });
    } else {
      navigate({
        to: "/w/$groupSlug/k/$kitUuid",
        params: { groupSlug, kitUuid },
      });
    }
  };

  const handleDeleteFolder = async () => {
    try {
      await deleteFolder(kitFolder.uuid);
      toast.success(`Folder "${kitFolder.name}" deleted.`);
      navigateToFirstFolderOrKitPage();
    } catch {
      toast.error(`Failed to delete folder "${kitFolder.name}"`);
    }
  };

  const handleUnlinkFolder = async () => {
    await unlinkFolder({
      folderUuid: kitFolder.uuid,
    });
    toast.success(`Folder "${kitFolder.name}" unlinked.`);
    navigateToFirstFolderOrKitPage();
  };

  const handleParseRemainingFiles = async () => {
    await parseRemainingFiles();
    toast.success(`Parsing remaining files.`);
  };

  const handleSyncFolder = async () => {
    if (!rootFolder) return;
    try {
      await syncFolder(rootFolder.uuid);
      toast.success("Sync started.");
    } catch {
      toast.error("Failed to start sync.");
    }
  };

  const renderBreadCrumbs = () => {
    if (!folder) return null;
    const crumbs: Folder[] = [];
    let currentFolder: Folder | null = folder;
    while (currentFolder !== null) {
      crumbs.unshift(currentFolder);
      currentFolder = currentFolder.parentFolderUuid
        ? foldersMap[currentFolder.parentFolderUuid]
        : null;
    }
    return (
      <div className="text-foreground-5 mb-4 flex flex-row items-center gap-2 text-sm">
        <FontAwesomeIcon icon={faFolders} />
        {crumbs.map((crumb, index) => (
          <>
            {index > 0 && <FontAwesomeIcon icon={faChevronRight} />}
            <Link
              key={crumb.uuid}
              to="/w/$groupSlug/k/$kitUuid/knowledge/$folderUuid/$"
              params={{
                groupSlug,
                kitUuid,
                folderUuid: crumbs[0].uuid,
                _splat: crumbs
                  .slice(1, index + 1)
                  .map((c) => c.uuid)
                  .join("/"),
              }}
              className={cn({
                "text-foreground font-bold": crumb.uuid === folder.uuid,
              })}
            >
              {crumb.name}
            </Link>
          </>
        ))}
      </div>
    );
  };

  const isRootFolder = !folder || folder.uuid === kitFolder.uuid;
  const canWrite = canWriteArtifact(kitFolder);
  const isOwned = isOwnedArtifact(kitFolder);
  const isLinkedExternalFolder =
    Boolean(rootFolder?.externalProvider) === false &&
    EXTERNAL_FOLDER_SYNC_PROVIDERS.includes(
      rootFolder?.externalProvider as (typeof EXTERNAL_FOLDER_SYNC_PROVIDERS)[number],
    );

  const canUpload = loadFiles && !isLinkedExternalFolder;

  return (
    <>
      <AppTopRightMenu.ContentPortal>
        {canUpload && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button disabled={!canWrite}>
                <FontAwesomeIcon icon={faPlus} />
                Add File
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={handleUploadClick}>
                <FontAwesomeIcon icon={faFile} /> Upload
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setDialogOpen("external-file-dialog")}
              >
                <FontAwesomeIcon icon={faCloud} /> From Cloud
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setAddFromLibraryOpen(true)}>
                <FontAwesomeIcon icon={faBooks} /> From Library
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}{" "}
        {loadFiles && isLinkedExternalFolder && (
          <Button
            variant="outline"
            onClick={handleSyncFolder}
            disabled={isSyncingFolder || isAnyFilePending}
          >
            <FontAwesomeIcon
              icon={faSync}
              className={
                isSyncingFolder ? "animate-[spin_2s_linear_infinite]" : ""
              }
            />
            {isSyncingFolder ? "Syncing..." : "Sync"}
          </Button>
        )}
        {isOwned && isRootFolder && (
          <GrantDialog
            groupUuid={groupUuid}
            objectName={kitFolder.name}
            folderUuid={kitFolder.uuid}
          />
        )}
        {isRootFolder && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                aria-label="More Actions"
                title="More Actions"
              >
                <FontAwesomeIcon icon={faEllipsisV} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <Tooltip>
                {(sharedGroups.length > 0 || !isOwned) && (
                  <TooltipContent>
                    {!isOwned && "Cannot delete a folder you do not own."}
                    {sharedGroups.length > 0 &&
                      "Cannot delete folder shared with other groups."}
                  </TooltipContent>
                )}
                <TooltipTrigger>
                  <DropdownMenuItem
                    disabled={!isOwned || sharedGroups.length > 0}
                    onClick={() => setDialogOpen("delete")}
                  >
                    <FontAwesomeIcon icon={faTrash} /> Delete
                  </DropdownMenuItem>
                </TooltipTrigger>
              </Tooltip>
              <DropdownMenuItem
                disabled={isOwned}
                onClick={() => setDialogOpen("unlink")}
              >
                <FontAwesomeIcon icon={faUnlink} /> Unlink
              </DropdownMenuItem>
              {kit?.type === "expert" &&
                creatorExpert &&
                subFolderPath.length === 0 && (
                  <DropdownMenuItem
                    onClick={() =>
                      isExpertFolder
                        ? dissociateFolder({ folderUuid: kitFolder.uuid })
                        : associateFolder({ folderUuid: kitFolder.uuid })
                    }
                  >
                    <FontAwesomeIcon
                      icon={isExpertFolder ? faUnlink : faBooks}
                    />
                    {isExpertFolder
                      ? "Remove from Expert Knowledge"
                      : "Set as Expert Knowledge"}
                  </DropdownMenuItem>
                )}
              {rootFolder && (
                <RoleGate systemRoles={["super"]} debug>
                  <DropdownMenuItem
                    onClick={handleParseRemainingFiles}
                    disabled={rootFolder.files.every(
                      (file) => file.isParsing || file.isSearchable,
                    )}
                  >
                    <FontAwesomeIcon icon={faRefresh} /> Parse Remaining Files
                  </DropdownMenuItem>
                </RoleGate>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </AppTopRightMenu.ContentPortal>

      <Page>
        <PageHeader>
          <PageHeader.Title>
            <EditableText
              value={kitFolder.name}
              onUpdate={handleUpdateTitle}
              disabled={!canWrite}
            />
          </PageHeader.Title>
        </PageHeader>
        <PageBody>
          <PageBody.Metadata className="justify-between">
            <PageBody.MetadataGroup>
              {rootFolder && <FileCountBadge count={files.length} />}
              {!canWrite && (
                <div className="flex flex-row items-center gap-2">
                  <FontAwesomeIcon icon={faLock} /> Read-Only
                </div>
              )}
              {isExpertFolder && (
                <div className="flex flex-row items-center gap-2">
                  <ExpertIcon /> Expert Knowledge
                </div>
              )}
              {rootFolder?.externalProvider === "GOOGLE_DRIVE" && (
                <div className="flex flex-row items-center gap-2">
                  <FontAwesomeIcon icon={faGoogleDrive} /> Google Drive
                </div>
              )}
              {rootFolder?.externalProvider === "SHAREPOINT" && (
                <div className="flex flex-row items-center gap-2">
                  <FontAwesomeIcon icon={faMicrosoft} /> SharePoint
                </div>
              )}
            </PageBody.MetadataGroup>
            <PageBody.MetadataGroup>
              {rootFolder?.dateSynced && (
                <div className="flex flex-row items-center gap-2">
                  <FontAwesomeIcon icon={faSync} />
                  {smartDate(rootFolder.dateSynced)}
                </div>
              )}
              <ArtifactMetadata artifact={kitFolder} />
            </PageBody.MetadataGroup>
          </PageBody.Metadata>

          {folder &&
            rootFolder &&
            folder.uuid !== rootFolder.uuid &&
            renderBreadCrumbs()}

          {(isSyncingFolder || isAnyFilePending) && rootFolder && (
            <div className="bg-accent flex items-center gap-2 rounded-md border px-4 py-2 text-sm text-white">
              <FontAwesomeIcon
                icon={isSyncingFolder ? faSync : faGear}
                className="animate-[spin_2s_linear_infinite]"
              />
              {isSyncingFolder &&
                (rootFolder.externalProvider === "SHAREPOINT"
                  ? "Syncing folder with SharePoint..."
                  : "Syncing folder with Google Drive...")}
              {isAnyFilePending && "Parsing files..."}
            </div>
          )}

          <div className="flex flex-row items-start gap-4">
            <div className="w-24 shrink-0 text-sm font-bold">Description:</div>
            <EditableText
              value={kitFolder.description || ""}
              disabled={!canWrite}
              onUpdate={handleUpdateDescription}
              placeholder={
                canWrite ? "Write folder description..." : "No description"
              }
            />
          </div>

          {folder && (
            <div className="flex flex-row items-start gap-4">
              <div className="w-24 shrink-0 text-sm font-bold">AI Summary:</div>
              <CollapsibleText maxRows={5}>
                <Markdown>{folder.summary || "No summary"}</Markdown>
              </CollapsibleText>
            </div>
          )}

          {childFolders.length > 0 && <SubFolders folders={subFolders} />}

          {loadFiles && folder && (
            <FolderFiles
              folderUuid={folder.uuid}
              kitUuid={kitUuid}
              groupSlug={groupSlug}
              groupUuid={groupUuid}
            />
          )}
        </PageBody>
      </Page>

      {loadFiles && folder && (
        <FileUploadDialog
          folderUuid={folder.uuid}
          kitUuid={kitUuid}
          groupUuid={groupUuid}
          ref={fileUploadDialogRef}
        />
      )}
      <ExternalFileBrowserDialog
        open={dialogOpen === "external-file-dialog"}
        onOpenChange={(open) =>
          setDialogOpen(open ? "external-file-dialog" : null)
        }
        groupUuid={groupUuid}
        mode="file"
        onSelected={handleExternalFileSelected}
      />
      <AddFromLibraryDialog
        open={addFromLibraryOpen}
        onOpenChange={setAddFromLibraryOpen}
        groupUuid={groupUuid}
        kitUuid={kitUuid}
        defaultFolderUuid={folder?.uuid ?? kitFolder.uuid}
      />
      <DeleteDialog
        name={kitFolder.name}
        open={dialogOpen === "delete"}
        onOpenChange={(open) => setDialogOpen(open ? "delete" : null)}
        onDeleteClick={handleDeleteFolder}
      />
      <BaseDialog
        title="Unlink Folder"
        description={`Are you sure you want to unlink the folder "${kitFolder.name}" from this kit? This action cannot be undone.`}
        open={dialogOpen === "unlink"}
        onOpenChange={(open) => setDialogOpen(open ? "unlink" : null)}
        onConfirmClick={handleUnlinkFolder}
        confirmLabel="Unlink"
      />
    </>
  );
};
